import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, Button } from '../../ui';
import { MaskedSurebetPreview } from '../../surebets';
import { MaskedBestOddsPreview } from '../../bestOdds';
import { CalculatorModal, CalculatorWrapper, openCalculatorFromSurebet } from '../../calculator';
import { FilterPanel } from '../../filters';
import EducationalContent from './EducationalContent';
import MarketingContent from './MarketingContent';
import TelegramPromoSection from './TelegramPromoSection';
import AcademyPromoSection from './AcademyPromoSection';
import { updateRouteMeta } from '../../navigation/utils/routeUtils';
import { ROUTE_PATHS } from '../../navigation/config/routeConfig';
import './LandingPage.css';

/**
 * LandingPage - Public landing page with masked surebet previews
 * Shows when user is not authenticated or doesn't have active subscription
 */
const LandingPage = ({ isAuthenticated = false, user = null, onLogout = null, onProfile = null }) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const calculatorRef = useRef(null);
  const [activeProduct, setActiveProduct] = useState('arbitrage'); // 'arbitrage' | 'bestOdds'

  const handleCalculatorClick = useCallback((surebet, marginKey) => {
    openCalculatorFromSurebet(calculatorRef, surebet, marginKey);
  }, []);

  // Update SEO meta tags when component mounts
  useEffect(() => {
    // Update route meta tags
    updateRouteMeta(ROUTE_PATHS.PUBLIC.HOME);

    // Update Open Graph tags
    const updateOGTag = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update Twitter Card tags (Twitter uses property attribute, not name)
    const updateTwitterTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set bilingual meta tags based on current language
    if (language === 'bg') {
      updateOGTag('og:title', 'SureBet - Арбитражни възможности');
      updateOGTag('og:description', 'Платформа за анализ на арбитражни възможности на спортни пазари');
      updateOGTag('og:locale', 'bg_BG');
      updateTwitterTag('twitter:title', 'SureBet - Арбитражни възможности');
      updateTwitterTag('twitter:description', 'Анализ на спортни арбитражни възможности и динамични пазарни данни.');
    } else {
      updateOGTag('og:title', 'SureBet - Arbitrage Opportunities');
      updateOGTag('og:description', 'Sports arbitrage opportunities analysis platform for the Bulgarian market.');
      updateOGTag('og:locale', 'en_US');
      updateTwitterTag('twitter:title', 'SureBet - Arbitrage Opportunities');
      updateTwitterTag('twitter:description', 'Arbitrage opportunities analysis and dynamic market data for sports events.');
    }

    // Cleanup function (optional - not needed for meta tags)
    return () => {
      // Meta tags persist, no cleanup needed
    };
  }, [language]);

  return (
    <PageContainer>
      <CalculatorWrapper calculatorRef={calculatorRef} />
      <ArbiWinHeader
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        onLogout={onLogout}
        onProfile={onProfile}
        onHomeClick={() => navigate('/')}
      />

      <MainContainer>
        {/* Hero Section */}
        <section className="landing-hero">
          <div className="landing-hero__content">
            <h1 className="landing-hero__title">
              {t('landing.hero.title')}
            </h1>
            <p className="landing-hero__subtitle">
              {t('landing.hero.subtitle')}
            </p>
            <div className="landing-hero__actions">
              <Button
                variant="primary"
                size="large"
                onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
              >
                {t('landing.hero.getStarted')}
              </Button>
            </div>

            <div
              className="landing-hero__product-toggle"
              role="group"
              aria-label={t('landing.heroToggle.ariaLabel', 'Превключи между ArbiWin продуктите')}
            >
              <button
                type="button"
                className={`landing-hero__product-toggle-btn ${activeProduct === 'arbitrage' ? 'landing-hero__product-toggle-btn--active' : ''}`}
                aria-pressed={activeProduct === 'arbitrage'}
                onClick={() => setActiveProduct('arbitrage')}
              >
                {t('landing.heroToggle.arbitrage')}
              </button>
              <button
                type="button"
                className={`landing-hero__product-toggle-btn ${activeProduct === 'bestOdds' ? 'landing-hero__product-toggle-btn--active' : ''}`}
                aria-pressed={activeProduct === 'bestOdds'}
                onClick={() => setActiveProduct('bestOdds')}
              >
                {t('landing.heroToggle.bestOdds')}
              </button>
            </div>
          </div>
        </section>

        {/* Filter Panel - Inactive mode for public users (Arbitrage only) */}
        {activeProduct === 'arbitrage' && (
          <section className="landing-filters">
            <FilterPanel
              inactive={true}
              showRefreshButton={false}
              collapsible={true}
            />
          </section>
        )}

        {/* Masked Previews - Real data from backend, product selected via hero toggle */}
        <section id="landing-previews" className="landing-previews">
          {activeProduct === 'arbitrage' ? (
            <MaskedSurebetPreview
              key="masked-preview-public"
              onActivate={() => navigate(isAuthenticated ? '/subscription' : '/register')}
              onCalculatorClick={handleCalculatorClick}
              showHeader={true}
              maxItems={5}
            />
          ) : (
            <MaskedBestOddsPreview
              key="masked-best-odds-preview-public"
              onActivate={() => navigate(isAuthenticated ? '/subscription' : '/register')}
              showHeader={true}
              maxItems={3}
            />
          )}
        </section>

        <TelegramPromoSection
          onCtaClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
        />

        {/* Testimonials / Social Proof Section */}
        <section className="landing-testimonials">
          <div className="landing-testimonials__container">
            <h2 className="landing-testimonials__title">{t('landing.testimonials.title')}</h2>
            <p className="landing-testimonials__subtitle">
              {t('landing.testimonials.subtitle')}
            </p>

            <div className="landing-testimonials__grid">
              <div className="landing-testimonial-card">
                <div className="landing-testimonial-card__content">
                  <div className="landing-testimonial-card__quote">
                    {t('landing.testimonials.testimonial1.quote')}
                  </div>
                  <div className="landing-testimonial-card__rating">
                    {'★★★★★'}
                  </div>
                </div>
              </div>

              <div className="landing-testimonial-card">
                <div className="landing-testimonial-card__content">
                  <div className="landing-testimonial-card__quote">
                    {t('landing.testimonials.testimonial2.quote')}
                  </div>
                  <div className="landing-testimonial-card__rating">
                    {'★★★★★'}
                  </div>
                </div>
              </div>

              <div className="landing-testimonial-card">
                <div className="landing-testimonial-card__content">
                  <div className="landing-testimonial-card__quote">
                    {t('landing.testimonials.testimonial3.quote')}
                  </div>
                  <div className="landing-testimonial-card__rating">
                    {'★★★★★'}
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="landing-testimonials__trust">
              <div className="landing-trust-item">
                <div className="landing-trust-item__value">{t('landing.testimonials.trust.users')}</div>
                <div className="landing-trust-item__label">{t('landing.testimonials.trust.usersLabel')}</div>
              </div>
              <div className="landing-trust-item">
                <div className="landing-trust-item__value">{t('landing.testimonials.trust.surebets')}</div>
                <div className="landing-trust-item__label">{t('landing.testimonials.trust.surebetsLabel')}</div>
              </div>
              <div className="landing-trust-item">
                <div className="landing-trust-item__value">{t('landing.testimonials.trust.satisfaction')}</div>
                <div className="landing-trust-item__label">{t('landing.testimonials.trust.satisfactionLabel')}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Marketing Content Section */}
        <section className="landing-marketing">
          <MarketingContent
            onRegisterClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
          />
        </section>

        {/* Pricing Section */}
        <section className="landing-pricing">
          <div className="landing-pricing__container">
            <h2 className="landing-pricing__title">{t('landing.pricing.title')}</h2>
            {t('landing.pricing.description') && (
              <p className="landing-pricing__description">
                {t('landing.pricing.description')}
              </p>
            )}

            <div className="landing-pricing__card">
              <div className="landing-pricing__price">
                <span className="landing-pricing__price-amount">
                  €17.99
                </span>
                <span className="landing-pricing__price-period">{t('landing.pricing.period')}</span>
              </div>

              <div className="landing-pricing__promo">
                <p className="landing-pricing__promo-text">{t('landing.pricing.promoText')}</p>
                <p className="landing-pricing__promo-disclaimer">{t('landing.pricing.promoDisclaimer')}</p>
              </div>

              <h4 className="landing-pricing__features-heading">{t('landing.pricing.featuresIncludes')}</h4>
              <ul className="landing-pricing__features">
                <li>{t('landing.pricing.feature1')}</li>
                <li>{t('landing.pricing.feature2')}</li>
                <li>{t('landing.pricing.feature3')}</li>
                <li>{t('landing.pricing.feature4')}</li>
                <li>{t('landing.pricing.feature5')}</li>
              </ul>

              <Button
                variant="primary"
                size="large"
                onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
                className="landing-pricing__cta"
              >
                {t('landing.pricing.cta')}
              </Button>
            </div>
          </div>
        </section>

        {/* Educational Content Section */}
        <section className="landing-educational">
          <EducationalContent
            onRegisterClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
          />
        </section>

        <AcademyPromoSection />

        {/* CTA Section */}
        <section className="landing-cta">
          <div className="landing-cta__container">
            <h2 className="landing-cta__title">{t('landing.cta.title')}</h2>
            <Button
              variant="primary"
              size="large"
              onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
            >
              {t('landing.cta.button')}
            </Button>
          </div>
        </section>
      </MainContainer>

      <ArbiWinFooter />
      <CalculatorModal />
    </PageContainer>
  );
};

LandingPage.propTypes = {
  isAuthenticated: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    role: PropTypes.string,
    name: PropTypes.string
  }),
  onLogout: PropTypes.func,
  onProfile: PropTypes.func
};

export default LandingPage;

